import {
  SCRIPT_PIPE_FOOTER,
  SCRIPT_PIPE_HEADER,
} from "@via/components/tree-structure/helpers/constants.tsx";
import {
  HandleTypes,
  NODE_TYPE_PREFIX,
  PipelineOperations,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { isPipelineExternal } from "@via/pages/projects/runs/utils/helpers.tsx";
import { compressFieldIfNeeded } from "@via/utils/compression";
import {
  EDGE_TYPES,
  EDGE_TYPE_REGEX,
  LANGUAGE_MODES,
  NODE_TYPES,
  PIPELINE_TYPES,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { isObjectEmpty } from "@via/utils/helpers.tsx";
import {
  AdvancedFilesInterface,
  EdgeInterface,
  NodeInterface,
  OutputParameterPayload,
  PipelineDraftData,
  PipelineDraftNode,
  ScriptPipeConfig,
} from "@via/utils/interfaces.tsx";
import { Connection, Edge, useUpdateNodeInternals } from "@xyflow/react";
import { AIModelType, CortexRegistryType, MenuGroupType } from "common";
import { ReactNode } from "react";
import Logo from "/logo.svg";
import NFCoreLogo from "/nf-core.png";

export const getNodeType = (type: string): string => {
  const first = type?.charAt(0);
  if (type === NODE_TYPE_PREFIX.Input) {
    return NODE_TYPES.InputNode;
  } else if (type === NODE_TYPE_PREFIX.Output) {
    return NODE_TYPES.OutputNode;
  } else if (first === NODE_TYPE_PREFIX.Module) {
    return NODE_TYPES.ModuleNode;
  } else if (first === NODE_TYPE_PREFIX.Model) {
    return NODE_TYPES.ModelNode;
  } else if (first === NODE_TYPE_PREFIX.Skill) {
    return NODE_TYPES.SkillNode;
  } else {
    return NODE_TYPES.ProcessNode;
  }
};

export const getNodeStyle = (type: string): string => {
  switch (type) {
    case "inputNode":
      return "input";
    case "outputNode":
      return "output";
    case "moduleNode":
      return "module";
    default:
      return "process";
  }
};

export const checkIsNewParameter = connection => {
  const { sourceHandle, targetHandle } = connection;
  return sourceHandle?.includes("inPara") || targetHandle?.includes("outPara");
};

export const checkContainsIOParameter = edgeId => {
  return edgeId?.includes("inPro") || edgeId?.includes("outPro");
};

export const formatNewIOParameterConnection = (obj: Connection): Connection => {
  const { sourceHandle, targetHandle } = obj;
  if (sourceHandle?.includes("inPara")) {
    const numberToReplace = sourceHandle.split("-")[3];
    const numberToReplaceWith = targetHandle?.split("-")?.[3] ?? "";
    obj.sourceHandle = sourceHandle.replace(
      numberToReplace,
      numberToReplaceWith
    );
    return obj;
  }
  if (targetHandle?.includes("outPara")) {
    const numberToReplace = targetHandle.split("-")[3];
    const numberToReplaceWith = sourceHandle?.split("-")?.[3] || "";
    obj.targetHandle = targetHandle.replace(
      numberToReplace,
      numberToReplaceWith
    );
    return obj;
  }
  return obj;
};

export const getDataIdType = type => {
  if (type === NODE_TYPES.ModuleNode) {
    return "pipelineId";
  } else if (type === NODE_TYPES.InputNode || type === NODE_TYPES.OutputNode) {
    return "parameterId";
  } else if (type === NODE_TYPES.ModelNode) {
    return "modelId";
  } else if (type === NODE_TYPES.SkillNode) {
    return "skillId";
  } else {
    return "processId";
  }
};

export const generateIOTooltips = (
  name: string = "",
  handles: string[] = [],
  parameters: any[] = []
) => {
  return handles
    .map((handle: string) => {
      const parameterId = parseInt(handle.split("-")?.[3], 10);
      const matchedParameter = parameters.find(
        param => param.id === parameterId
      );

      if (!matchedParameter) return null;

      const { qualifier, name: identifier, fileType } = matchedParameter;
      return {
        name,
        identifier,
        qualifier,
        fileType,
      };
    })
    .filter(Boolean);
};

// io is comming from process_parameter table. So the order of this data is important
export const generateModuleTooltips = (
  handles,
  processes,
  io,
  matchingNodes
) => {
  const tooltips: Record<string, unknown>[] = [];
  for (let i = 0; i < handles.length; i++) {
    const handle = handles[i];

    const parts = handle.split("-");
    const type = parts[0];
    const processId = parseInt(parts[1], 10);
    const orderId = parseInt(parts[2], 10);
    const parameterId = parseInt(parts[3], 10);
    let mappedProcessNodeOrder: number | null = null;
    const matchingNodesIDs: string[] = matchingNodes.get(handle) || [];
    if (matchingNodesIDs && matchingNodesIDs.length > 0) {
      //here matchingNodes.get might return multiple nodes belong to multiple process. we need to take the ones with correct process id
      const filteredMatchingNodes = matchingNodesIDs.filter(nodeId => {
        const nodeProcessId = parseInt(nodeId.split("-")[1], 10);
        return nodeProcessId === processId;
      });
      if (filteredMatchingNodes.length > 0) {
        mappedProcessNodeOrder = parseInt(
          filteredMatchingNodes[0].split("-")[2],
          10
        );
      }
    }

    const matchedProcessParamsByParamId = io.filter(
      param =>
        param?.parameter_id === parameterId && param?.processId === processId
    );
    let matchedProcessParam = matchedProcessParamsByParamId?.[0];
    const matchedAllProcessParams = io.filter(
      param => param?.processId === processId
    );
    if (
      mappedProcessNodeOrder !== null &&
      matchedAllProcessParams[mappedProcessNodeOrder] &&
      matchedAllProcessParams.length > 1
    ) {
      matchedProcessParam = matchedAllProcessParams[mappedProcessNodeOrder];
    }

    const process = processes?.filter(
      process => process?.orderId === orderId && process?.type === type
    );

    if (matchedProcessParam) {
      const { sname, name, qualifier, file_type } = matchedProcessParam;
      tooltips.push({
        process: process?.map(p => p?.name).join(","),
        identifier: name,
        name: sname,
        fileType: file_type,
        qualifier,
      });
    }
  }
  return tooltips;
};

/**
 * It will return list of processes of a module with correct name, orderId and type
 * OrderId adds uniqueness to the process to create a module tooltip
 * Type is used to differentiate between input and output processes
 * @param pipeline all the pipeline data including main_pipeline_id, process_id, etc.
 * @param matchingNodes should be key value pair of moduleNodeId and pipelineNodeIds
 * @param nodes nodes should be formatted
 * @returns list of processes
 */
export const getProcessesOfPipeline = (pipeline, matchingNodes, nodes) => {
  const processes: any = [];
  if (matchingNodes?.size === 0) return [];
  for (const [moduleNodeId, pipelineNodeIds] of matchingNodes) {
    for (const id of pipelineNodeIds) {
      const type = moduleNodeId?.split("-")?.[0];
      const processId = parseInt(id.split("-")?.[1]);
      const orderId = parseInt(moduleNodeId?.split("-")?.[2]);
      const gNum = parseInt(id.split("-")?.[4]);
      const nodeName = nodes[`g-${gNum}`]?.nodeName;
      processes.push({
        ...pipeline[`process_${processId}`],
        name: nodeName,
        orderId,
        type,
      });
    }
  }
  return processes;
};

/**
 *
 * @param pipeline all pipeline data
 * @returns list the all input/output of parameters of a process
 */
export const getIOOfProcess = pipeline => {
  return Object.keys(pipeline)
    .filter(key => key.startsWith("pro_para"))
    .reduce((acc: any, key) => {
      const processId = parseInt(key?.split("_")?.[3]);
      const parameterType = key?.split("_")?.[2];
      if (Array.isArray(pipeline[key])) {
        acc.push(
          ...(pipeline[key] ?? []).map(item => ({
            ...item,
            processId,
            parameterType,
          }))
        );
      } else {
        acc.push(pipeline[key], processId, parameterType);
      }
      return acc;
    }, []);
};

export const createHandles = (
  node,
  pipeline = {},
  id,
  edges: any[] = [],
  parameters
) => {
  if (
    (node?.inputHandles && node?.inputHandles.length > 0) ||
    (node?.outputHandles && node?.outputHandles.length > 0)
  ) {
    const { inputHandles, outputHandles, tooltips } = node;
    return {
      inputHandles,
      outputHandles,
      inputTooltips: tooltips?.inputs,
      outputTooltips: tooltips?.outputs,
    };
  }
  if (node?.type === NODE_TYPES.ProcessNode) {
    const inputKey = `pro_para_inputs_${node?.param}`;
    const outputKey = `pro_para_outputs_${node?.param}`;

    const inputs = pipeline[inputKey] || [];
    const outputs = pipeline[outputKey] || [];

    const inputHandles = inputs.map((input, i) => {
      return `i-${node?.param}-${i}-${input?.parameter_id}-${id}`;
    });
    const outputHandles = outputs.map((output, i) => {
      return `o-${node?.param}-${i}-${output?.parameter_id}-${id}`;
    });
    const inputTooltips = inputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier,
        fileType: file_type,
      })
    );
    const outputTooltips = outputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier: qualifier,
        fileType: file_type,
      })
    );

    return {
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips,
    };
  } else if (node?.type === NODE_TYPES.InputNode) {
    const outputHandles = Array.from(
      new Set(
        edges
          .filter(
            edge =>
              edge.sourceHandle &&
              edge.sourceHandle.includes("inPro") &&
              edge.sourceHandle.split("-")[4] === id
          )
          .map(edge => edge.sourceHandle)
      )
    );
    if (outputHandles.length === 0) {
      outputHandles.push(`o-inPro-1-inPara-${id}`);
    }
    const outputTooltips = generateIOTooltips(
      node?.nodeName,
      outputHandles,
      parameters
    );
    return { outputHandles, outputTooltips };
  } else if (node?.type === NODE_TYPES.OutputNode) {
    const inputHandles = Array.from(
      new Set(
        edges
          .filter(
            edge =>
              edge.targetHandle &&
              edge.targetHandle.includes("outPro") &&
              edge.targetHandle.split("-")[4] === id
          )
          .map(edge => edge.targetHandle)
      )
    );
    if (inputHandles.length === 0) {
      inputHandles.push(`i-outPro-1-outPara-${id}`);
    }
    const inputTooltips = generateIOTooltips(
      node?.nodeName,
      inputHandles,
      parameters
    );
    return { inputHandles, inputTooltips };
  } else if (node?.type === NODE_TYPES.ModuleNode) {
    const pipelineId = extractAndParseInt(node?.param);
    const key = `pipeline_module_${pipelineId}`;
    const selected = pipeline[key];
    const parsedEdges = cleanAndParse(selected?.edges) ?? [];
    const parsedNodes = cleanAndParse(selected?.nodes) ?? {};
    const { edges: moduleEdges, matchingNodes } = processNewModuleEdges(
      parsedEdges,
      id,
      pipelineId
    );
    const formattedEdges = createNewModuleEdges(moduleEdges, id) ?? [];
    const formattedNodes = formatNodesDraft(parsedNodes);
    const processes =
      getProcessesOfPipeline(pipeline, matchingNodes, formattedNodes) ?? [];
    const io = getIOOfProcess(pipeline) ?? [];
    const inputIO = io.filter(i => i.parameterType === "inputs");
    const outputIO = io.filter(i => i.parameterType === "outputs");
    const inputHandles =
      formattedEdges
        .filter(edge => edge.id.startsWith("pi"))
        .map(edge => edge.id) ?? [];
    const outputHandles =
      formattedEdges
        .filter(edge => edge.id.split("-")[0] === "po")
        .map(edge => edge.id) ?? [];
    const inputTooltips = generateModuleTooltips(
      inputHandles,
      processes,
      inputIO,
      matchingNodes
    );
    const outputTooltips = generateModuleTooltips(
      outputHandles,
      processes,
      outputIO,
      matchingNodes
    );
    return {
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips,
    };
  } else if (
    node?.type === NODE_TYPES.ModelNode ||
    node?.type === NODE_TYPES.SkillNode
  ) {
    const inputKey = `pro_para_inputs_${node?.param}`;
    const outputKey = `pro_para_outputs_${node?.param}`;

    const inputs = pipeline[inputKey] || [];
    const outputs = pipeline[outputKey] || [];

    const inputHandles = inputs.map((input, i) => {
      return `i-${node?.param}-${i}-${input?.parameter_id}-${id}`;
    });
    const outputHandles = outputs.map((output, i) => {
      return `o-${node?.param}-${i}-${output?.parameter_id}-${id}`;
    });
    const inputTooltips = inputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier,
        fileType: file_type,
      })
    );
    const outputTooltips = outputs.map(
      ({ name, sname, qualifier, file_type }) => ({
        identifier: name,
        name: sname,
        qualifier: qualifier,
        fileType: file_type,
      })
    );

    return {
      inputHandles,
      outputHandles,
      inputTooltips,
      outputTooltips,
    };
  }
};

export const getDataId = param => {
  if (param?.charAt(0) === "p") {
    return extractAndParseInt(param);
  } else {
    return param;
  }
};

/** * To understand what each value means, check out the guide on Notion
 https://www.notion.so/32-04-Pipeline-visualization-253b35eb10a748babeb9177562d05e69 ***/
export const formatNodesData = (draft, pipeline, edges, parameters): any => {
  const { nodes } = draft;
  if (isObjectEmpty(nodes) || !nodes) {
    return [];
  }
  return Object.keys(nodes).map(key => {
    const node = nodes[key];
    const id = key?.split("-")[1];
    const handles = createHandles(node, pipeline, id, edges, parameters);
    const dataIdType = getDataIdType(node?.type);
    const tooltips = {
      inputs: handles?.inputTooltips ?? [],
      outputs: handles?.outputTooltips ?? [],
    };
    return {
      id,
      name: node?.nodeName,
      type: node?.type,
      data: {
        label: node?.nodeName,
        type: node?.type,
        className: getNodeStyle(node?.type),
        [dataIdType]: node?.param,
        tooltips,
        settings: node?.nodeSettings,
        inputHandles: handles?.inputHandles ?? [],
        outputHandles: handles?.outputHandles ?? [],
      },
      position: { x: node?.xCoordinate, y: node?.yCoordinate },
    };
  });
};

// Extract numbers from the "edges" string to create edge objects
const getNumbers = (s: string): string => s?.match(/\d+$/)?.[0] ?? "";

export const formatEdgesData = ({ edges }): EdgeInterface[] | [] => {
  if (edges?.length === 0 || !edges) {
    return [];
  }

  return edges.map(e => {
    const [firstPart, secondPart] = e.split("_");
    // Matching on first letter for input/output of processes
    const firstCharOfFirstPart = firstPart?.split("-")?.[0];
    const secondCharOfSecondPart = secondPart?.split("-")?.[1];
    // Matching on first letter and next chunk for input/output nodes
    const firstChunk = firstPart?.split("-", 3).slice(0, 2).join("-");

    const firstSplit = firstPart?.split("-");
    const secondSplit = secondPart?.split("-");

    return mapEdge(
      firstPart,
      secondPart,
      firstSplit,
      secondSplit,
      firstChunk,
      firstCharOfFirstPart,
      secondCharOfSecondPart
    );
  });
};

const mapEdge = (
  firstPart: string = "",
  secondPart: string = "",
  firstSplit: string[] = [],
  secondSplit: string[] = [],
  firstChunk: string = "",
  firstCharOfFirstPart: string = "",
  secondCharOfSecondPart: string = ""
): EdgeInterface | object => {
  const commonFields = {
    id: `${firstPart}_${secondPart}`,
    type: "deleteButtonEdge",
  };
  switch (firstChunk) {
    case "i-outPro": // output node (second part is target)
      return {
        ...commonFields,
        node: firstSplit[4],
        parameterId: parseInt(firstSplit[3]),
        source: getNumbers(secondPart),
        target: getNumbers(firstPart),
        sourceHandle: secondPart,
        targetHandle: firstPart,
      };
    case "o-inPro": // input node (first part is source)
      return {
        ...commonFields,
        node: firstSplit[4],
        parameterId: parseInt(firstSplit[3]),
        source: getNumbers(firstPart),
        target: getNumbers(secondPart),
        sourceHandle: firstPart,
        targetHandle: secondPart,
      };
  }
  switch (firstCharOfFirstPart) {
    case "o": // Output node edge
      return {
        ...commonFields,
        node: firstSplit[4],
        parameterId: parseInt(firstSplit[3]),
        source: getNumbers(firstPart),
        target: getNumbers(secondPart),
        sourceHandle: firstPart,
        targetHandle: secondPart,
      };
    case "i": // Input node edge
      return {
        ...commonFields,
        node: firstSplit[4],
        parameterId: parseInt(firstSplit[3]),
        source: getNumbers(secondPart),
        target: getNumbers(firstPart),
        sourceHandle: secondPart,
        targetHandle: firstPart,
      };
    case "po": // Output node edge
      return {
        ...commonFields,
        node: firstSplit[4],
        pipelineId: parseInt(firstSplit[1]),
        processId: parseInt(firstSplit[3]),
        source: getNumbers(firstPart),
        target: getNumbers(secondPart),
        sourceHandle: firstPart,
        targetHandle: secondPart,
      };
    case "pi": // Input node edge
      return {
        ...commonFields,
        node: firstSplit[4],
        pipelineId: parseInt(firstSplit[1]),
        processId: parseInt(firstSplit[3]),
        source: getNumbers(secondPart),
        target: getNumbers(firstPart),
        sourceHandle: secondPart,
        targetHandle: firstPart,
      };
  }
  switch (secondCharOfSecondPart) {
    case "o": // Output node edge
      return {
        ...commonFields,
        node: secondSplit[4],
        parameterId: parseInt(secondSplit[3]),
        source: getNumbers(firstPart),
        target: getNumbers(secondPart),
        sourceHandle: secondPart,
        targetHandle: firstPart,
      };
    case "i": // Input node edge
      return {
        ...commonFields,
        node: secondSplit[4],
        parameterId: parseInt(secondSplit[3]),
        source: getNumbers(secondPart),
        target: getNumbers(firstPart),
        sourceHandle: firstPart,
        targetHandle: secondPart,
      };
    case "pi": // Process input edge
      return {
        ...commonFields,
        node: secondSplit[4],
        pipelineId: parseInt(secondSplit[1]),
        processId: parseInt(secondSplit[3]),
        source: getNumbers(secondPart),
        target: getNumbers(firstPart),
        sourceHandle: firstPart,
        targetHandle: secondPart,
      };
    case "po": // Process output edge
      return {
        ...commonFields,
        node: secondSplit[4],
        pipelineId: parseInt(secondSplit[1]),
        processId: parseInt(secondSplit[3]),
        source: getNumbers(firstPart),
        target: getNumbers(secondPart),
        sourceHandle: secondPart,
        targetHandle: firstPart,
      };
  }
  return {};
};

const formatNodeSettings = settings => {
  if (!settings) {
    return {};
  }
  // "" -> []
  // "2" -> [2]
  // ["2"] -> [2]

  if (
    settings.pubWebApp &&
    settings.pubWebApp.app &&
    typeof settings.pubWebApp.app === "string"
  ) {
    settings.pubWebApp.app = [parseInt(settings.pubWebApp.app)];
  } else if (settings.pubWebApp && settings.pubWebApp.app == "") {
    settings.pubWebApp.app = [];
    // if it is array turn into integers. But before send it to the backend we need to convert it to string
  } else if (
    settings.pubWebApp &&
    settings.pubWebApp.app &&
    Array.isArray(settings.pubWebApp.app)
  ) {
    settings.pubWebApp.app = settings.pubWebApp.app.map(app => parseInt(app));
  }
  return settings;
};

export const formatNodesDraft = inputData => {
  const nodes = {};

  for (const key in inputData) {
    if (inputData) {
      const value = inputData[key];
      const type = getNodeType(value[2].toString());
      const nodeName =
        type === NODE_TYPES.ModuleNode ? cleanProcessName(value[3]) : value[3];
      nodes[key] = {
        type,
        xCoordinate: value[0],
        yCoordinate: value[1],
        param: extractAndParseInt(value[2]),
        nodeName,
        nodeSettings: formatNodeSettings(value[4]),
        tooltips: {
          inputs: [],
          outputs: [],
        },
        inputHandles: [],
        outputHandles: [],
      };
    }
  }
  return nodes;
};

export const extractAndParseInt = str => {
  if (typeof str === "string") {
    const match = str?.match(/\d+/); // Find the digits in the string
    if (match) {
      return parseInt(match[0]); // Convert the matched digits to an integer
    }
    return str;
  }
  return str;
};

/**
 * * This is a starting point for the pipeline data
 * * What backend sends is a string, so we need to convert it to an object
 */
export const formatPipelineDraft = (pipeline, role, updateAllowed) => {
  return {
    id: pipeline?.id,
    name: pipeline?.name,
    summary: pipeline?.summary,
    dateCreated: pipeline?.date_created,
    dateModified: pipeline?.date_modified,
    description: pipeline?.description,
    mainG: pipeline?.mainG?.mainG,
    edges: pipeline?.edges?.edges ?? [],
    nodes: formatNodesDraft(pipeline?.nodes),
    isEditingAllowed: updateAllowed,
    isAdmin: role === USER_ROLES.ADMIN,
    isOwner: pipeline?.own === 1,
    revComment: pipeline?.rev_comment,
    writeGroupIds: pipeline?.write_group_id?.length
      ? pipeline.write_group_id
          .split(",")
          .map(Number)
          .filter(id => id !== 0) // Remove zeros
      : [],
    groupId: pipeline?.group_id,
    scriptPipeConfig: pipeline?.script_pipe_config,
    extractedPipeScript: pipeline?.extracted_pipe_script,
    scriptPipeHeader: pipeline?.script_pipe_header,
    scriptPipeFooter: pipeline?.script_pipe_footer,
    scriptModeHeader: pipeline?.script_mode_header,
    scriptModeFooter: pipeline?.script_mode_footer,
    pipelineGroupId: pipeline?.pipeline_group_id,
    pipelineUUId: pipeline?.pipeline_uuid,
    pipelineRevUUId: pipeline?.pipeline_rev_uuid,
    publishWebDir: pipeline?.publish_web_dir ?? "",
    publishWebAppDir: pipeline?.publish_web_app_dir ?? "",
    publishDMetaDir: pipeline?.publish_dmeta_dir ?? "",
    pipelineType: pipeline?.pipeline_type,
    rootId: pipeline?.root_id,
    importUrl: pipeline?.import_url,
    importVersion: pipeline?.import_version,
    version: pipeline?.version ?? "1.0.0",
    permission: pipeline?.perms,
    publiclySearchable: pipeline?.publicly_searchable === "true",
    useParamFiles: pipeline?.use_paramsfile,
    core: pipeline?.core,
    pinOrder: pipeline?.pin_order,
    pin: pipeline?.pin === "true",
    workflowStatus: pipeline?.workflow_status,
    origin: pipeline?.origin,
    details: pipeline?.details,
  };
};

export const getCorrectParam = data => {
  if (data?.processId) {
    return data.processId;
  } else if (data?.modelId) {
    return data.modelId;
  } else if (data?.skillId) {
    return data.skillId;
  } else if (data?.pipelineId) {
    return data.pipelineId;
  } else if (data?.type === NODE_TYPES.InputNode) {
    return "inPro";
  } else if (data?.type === NODE_TYPES.OutputNode) {
    return "outPro";
  }
};

/**
 * * This function is used to update the pipelineDraft when flow changes
 * * Adding new nodes, updating existing nodes, and removing nodes except changing the position
 * * Creating new edges, updating existing edges, and removing edges
 * @param draft PipelineDraft
 * @param current FlowData
 * @returns PipelineDraft
 */
export const formatWorkflowToDraftPayload = (draft, current) => {
  const { nodes, edges, viewport } = current;
  const latestNodes = nodes?.reduce((acc, node) => {
    acc[`g-${node.id}`] = {
      nodeName: node.data?.label,
      type: node.type,
      xCoordinate: node.position.x,
      yCoordinate: node.position.y,
      param: getCorrectParam(node.data),
      nodeSettings: node.data.settings,
      tooltips: node.data?.tooltips,
      inputHandles: node.data?.inputHandles ?? [],
      outputHandles: node.data?.outputHandles ?? [],
    };
    return acc;
  }, {});
  // Always sort the o-inPro and i-outPro to the "left"
  const latestEdges = edges?.map(obj => {
    const { id } = obj;
    const parts = id.split("_");
    const first = parts.find(
      part => part.includes("o-inPro") || part.includes("i-outPro")
    );
    const second = parts.find(
      part => !part.includes("o-inPro") && !part.includes("i-outPro")
    );
    return first && second ? `${first}_${second}` : id;
  });

  const mainG = [viewport?.x, viewport?.y, viewport?.zoom];
  const publishWebDir = nodes
    .filter(node => node.data?.settings?.pubWeb)
    .map(item => item?.data?.label)
    .join(",");

  return {
    ...draft,
    mainG,
    nodes: latestNodes,
    edges: latestEdges,
    publishWebDir,
  };
};

export const createNewIOEdges = (type, gNum): any => {
  if (type === NODE_TYPES.InputNode) {
    return { outputHandles: [`o-inPro-1-inPara-${gNum}`] };
  } else if (type === NODE_TYPES.OutputNode) {
    return { inputHandles: [`i-outPro-1-outPara-${gNum}`] };
  }
};

export const createNewProcessEdges = (data, gNum): EdgeInterface[] => {
  const { inputs, outputs } = data;
  const inputsPP =
    inputs?.map((input, i): any => {
      const { id, parameter_id } = input;
      const identifier = `i-${data.id}-${i}-${parameter_id}-${gNum}`;
      return {
        id: identifier,
        type: "deleteButtonEdge",
        node: gNum,
        data: input,
        processId: id,
        parameterId: parameter_id,
        source: null,
        target: gNum,
        sourceHandle: null,
        targetHandle: identifier,
      };
    }) ?? [];
  const outputsPP =
    outputs?.map((output, i): any => {
      const { id, parameter_id } = output;
      const identifier = `o-${data.id}-${i}-${parameter_id}-${gNum}`;
      return {
        id: identifier,
        type: "deleteButtonEdge",
        node: gNum,
        data: output,
        processId: id,
        parameterId: parameter_id,
        source: gNum,
        target: null,
        sourceHandle: identifier,
        targetHandle: null,
      };
    }) ?? [];
  return [...inputsPP, ...outputsPP];
};

export const createNewModuleEdges = (chars, gNum): any => {
  return chars?.map((c): any => {
    const split = c.split("-");
    const identifier = split[0];
    if (identifier === "po") {
      return {
        id: c,
        node: gNum,
        pipelineId: parseInt(split[1]),
        processId: parseInt(split[3]),
        source: gNum,
        target: null,
        sourceHandle: c,
        targetHandle: null,
      };
    } else if (identifier === "pi") {
      return {
        id: c,
        node: gNum,
        pipelineId: parseInt(split[1]),
        processId: parseInt(split[3]),
        source: null,
        target: gNum,
        sourceHandle: null,
        targetHandle: c,
      };
    }
    return {};
  });
};

/**
 * * This function is used to get the correct&valid edges and nodes
 * @param draft PipelineDraft
 * @param pipeline Pipeline
 * @param parameters Parameters[]
 * @returns nodes[],edges[]
 */
export const formatAllPipelineData = (draft, pipeline, parameters): any => {
  const draftEdgesFormatted = formatEdgesData({ edges: draft?.edges });
  const nodesFormatted = formatNodesData(
    draft,
    pipeline,
    draftEdgesFormatted,
    parameters
  );
  const validatedEdges = validateEdges(
    draftEdgesFormatted,
    nodesFormatted,
    pipeline
  );
  const updatedNodes = updateNodesTooltips(nodesFormatted, validatedEdges);

  if (nodesFormatted?.length || draftEdgesFormatted?.length) {
    return {
      nodesFormatted: updatedNodes,
      edgesFormatted: validatedEdges,
    };
  }
};

/**
 *  * This function is used to update the node tooltips data
 *  * Includes the nodes that have edges and the nodes that don't have edges
 * @param nodesFormatted NodeInterface[]
 * @param validatedEdges EdgeInterface[]
 * @returns NodeInterface[]
 */
const updateNodesTooltips = (
  nodesFormatted: NodeInterface[],
  validatedEdges: EdgeInterface[]
): NodeInterface[] => {
  const nodesThatHaveEdges = validatedEdges.map(edge => edge.node) ?? [];
  const nodesWithoutEdges =
    nodesFormatted.filter(
      node =>
        (node.type === NODE_TYPES.InputNode ||
          node.type === NODE_TYPES.OutputNode) &&
        !nodesThatHaveEdges.includes(node.id)
    ) ?? [];
  if (nodesWithoutEdges.length) {
    return nodesFormatted.map(node => {
      if (nodesWithoutEdges.find(noEdgeNode => noEdgeNode.id === node.id)) {
        return updateAbandonedNode(node);
      } else {
        return node;
      }
    });
  } else {
    return nodesFormatted;
  }
};

/**
 *  * This function is used to update the node tooltips data when the node doesn't have any edges
 * @param node NodeInterface
 * @returns NodeInterface
 */
const updateAbandonedNode = node => {
  if (node.type === NODE_TYPES.InputNode) {
    return {
      ...node,
      data: {
        ...node.data,
        inputHandles: [],
        outputHandles: [`o-inPro-1-inPara-${node.id}`],
        tooltips: {
          inputs: [],
          outputs: [{ connect: "Connect to Input" }],
        },
      },
    };
  } else if (node.type === NODE_TYPES.OutputNode) {
    return {
      ...node,
      data: {
        ...node.data,
        inputHandles: [`i-outPro-1-outPara-${node.id}`],
        outputHandles: [],
        tooltips: {
          inputs: [{ connect: "Connect to Output" }],
          outputs: [],
        },
      },
    };
  }
  return node;
};

/**
 * * This function is used to check the edge type and return the correct edge type
 * @param edge string
 * @returns EDGE_TYPES | null
 */
function checkEdgeType(edge: string): EDGE_TYPES | null {
  if (new RegExp(EDGE_TYPE_REGEX.OutputModuleToInputModule).test(edge)) {
    return EDGE_TYPES.OutputModuleToInputModule;
  } else if (new RegExp(EDGE_TYPE_REGEX.InputModuleToOutputModule).test(edge)) {
    return EDGE_TYPES.InputModuleToOutputModule;
  } else if (
    new RegExp(EDGE_TYPE_REGEX.OutputModuleToInputProcess).test(edge)
  ) {
    return EDGE_TYPES.OutputModuleToInputProcess;
  } else if (
    new RegExp(EDGE_TYPE_REGEX.InputModuleToOutputProcess).test(edge)
  ) {
    return EDGE_TYPES.InputModuleToOutputProcess;
  } else if (
    new RegExp(EDGE_TYPE_REGEX.OutputProcessToInputModule).test(edge)
  ) {
    return EDGE_TYPES.OutputProcessToInputModule;
  } else if (
    new RegExp(EDGE_TYPE_REGEX.InputProcessToOutputModule).test(edge)
  ) {
    return EDGE_TYPES.InputProcessToOutputModule;
  } else if (new RegExp(EDGE_TYPE_REGEX.OutputNodeToOutputModule).test(edge)) {
    return EDGE_TYPES.OutputNodeToOutputModule;
  } else if (new RegExp(EDGE_TYPE_REGEX.InputNodeToInputModule).test(edge)) {
    return EDGE_TYPES.InputNodeToInputModule;
  } else if (new RegExp(EDGE_TYPE_REGEX.InputNodeToInputProcess).test(edge)) {
    return EDGE_TYPES.InputNodeToInputProcess;
  } else if (new RegExp(EDGE_TYPE_REGEX.OutputNodeToOutputProcess).test(edge)) {
    return EDGE_TYPES.OutputNodeToOutputProcess;
  } else if (
    new RegExp(EDGE_TYPE_REGEX.OutputProcessToInputProcess).test(edge)
  ) {
    return EDGE_TYPES.OutputProcessToInputProcess;
  } else if (
    new RegExp(EDGE_TYPE_REGEX.InputProcessToOutputProcess).test(edge)
  ) {
    return EDGE_TYPES.InputProcessToOutputProcess;
  }
  return null;
}

/**
 * * This function is used to check if the handle is allowed to be connected
 * @param selectedPipeline PipelineInterface
 * @param handles string[]
 * @param edgeSplitted string
 * @returns boolean
 */
const allowHandle = (
  selectedPipeline,
  handles: string[],
  edgeSplitted: string
): boolean => {
  const gNum = edgeSplitted.split("-")?.[4];
  const checkIfHandleExists = handles.includes(edgeSplitted);
  if (!checkIfHandleExists) {
    return false;
  }
  // if the process has changed, we need to check if there is only one node with the same parameterId
  const processHasChanged =
    selectedPipeline?.changedProcessData?.[`tooltipsDiff_${gNum}`];
  const sameParamHandles = handles.filter(
    handle => handle.split("-")[3] === edgeSplitted.split("-")[3]
  );
  const allowMultipleSameParamHandles = processHasChanged
    ? sameParamHandles.length === 1
    : true;
  return allowMultipleSameParamHandles;
};

/**
 *  * This function is used to validate the edges. It checks if the edge exists or not based on the edge type
 *  * If the edge exists, it returns the edge
 *  * If the edge doesn't exist, it finds the possible match and returns the new edge
 * @param edge EdgeInterface
 * @param nodes NodeInterface[]
 * @param edgeType EDGE_TYPES
 * @param validEdges EdgeInterface[]
 * @param selectedPipeline PipelineInterface - contains all process revisions
 * @returns EdgeInterface
 */
const checkIsEdgeExist = (
  edge,
  nodes,
  edgeType,
  validEdges,
  selectedPipeline
) => {
  let firstModuleNode,
    secondModuleNode,
    firstProcessNode,
    secondProcessNode,
    possibleMatchFirstPart,
    possibleMatchSecondPart,
    inputHandles,
    outputHandles;

  const [firstPart, secondPart] = (edge.id ?? "").split("_");
  const firstGnum = firstPart?.split("-")?.[4];
  const secondGnum = secondPart?.split("-")?.[4];
  const firstCharOfFirstPart = firstPart?.split("-")?.[0];
  const firstChunk = firstPart?.split("-", 3)?.slice(0, 2)?.join("-");
  const firstSplit = firstPart?.split("-");

  switch (edgeType) {
    case EDGE_TYPES.InputNodeToInputProcess:
      secondProcessNode = nodes.filter(node => node.id == secondGnum);
      if (!secondProcessNode || secondProcessNode.length === 0) {
        console.log("Process node not found:", edge);
      }
      inputHandles = secondProcessNode
        .map(node => node.data.inputHandles)
        .flat();

      if (allowHandle(selectedPipeline, inputHandles, secondPart)) {
        return edge;
      } else {
        possibleMatchSecondPart = findPossibleNodesToConnect(
          secondPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
        if (possibleMatchSecondPart) {
          const newEdge = mapEdge(
            firstPart,
            possibleMatchSecondPart,
            firstSplit,
            possibleMatchSecondPart.split("-"),
            firstChunk,
            firstCharOfFirstPart,
            possibleMatchSecondPart.split("-")[1]
          );
          return newEdge;
        }
      }
      break;
    case EDGE_TYPES.OutputNodeToOutputProcess:
      secondProcessNode = nodes.filter(node => node.id == secondGnum);
      if (!secondProcessNode || secondProcessNode.length === 0) {
        console.log("Process node not found:", edge);
      }
      outputHandles = secondProcessNode
        .map(node => node.data.outputHandles)
        .flat();
      if (allowHandle(selectedPipeline, outputHandles, secondPart)) {
        return edge;
      } else {
        possibleMatchSecondPart = findPossibleNodesToConnect(
          secondPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
        if (possibleMatchSecondPart) {
          const newEdge = mapEdge(
            firstPart,
            possibleMatchSecondPart,
            firstSplit,
            possibleMatchSecondPart.split("-"),
            firstChunk,
            firstCharOfFirstPart,
            possibleMatchSecondPart.split("-")[1]
          );
          return newEdge;
        }
      }
      break;
    case EDGE_TYPES.InputProcessToOutputProcess:
      firstProcessNode = nodes.filter(node => node.id == firstGnum);
      secondProcessNode = nodes.filter(node => node.id == secondGnum);
      if (
        !firstProcessNode ||
        firstProcessNode.length === 0 ||
        !secondProcessNode ||
        secondProcessNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      inputHandles = firstProcessNode
        .map(node => node.data.inputHandles)
        .flat();
      outputHandles = secondProcessNode
        .map(node => node.data.outputHandles)
        .flat();

      if (allowHandle(selectedPipeline, inputHandles, firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = findPossibleNodesToConnect(
          firstPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (allowHandle(selectedPipeline, outputHandles, secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = findPossibleNodesToConnect(
          secondPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.OutputProcessToInputProcess:
      firstProcessNode = nodes.filter(node => node.id == firstGnum);
      secondProcessNode = nodes.filter(node => node.id == secondGnum);
      if (
        !firstProcessNode ||
        firstProcessNode.length === 0 ||
        !secondProcessNode ||
        secondProcessNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      outputHandles = firstProcessNode
        .map(node => node.data.outputHandles)
        .flat();
      inputHandles = secondProcessNode
        .map(node => node.data.inputHandles)
        .flat();

      if (allowHandle(selectedPipeline, outputHandles, firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = findPossibleNodesToConnect(
          firstPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (allowHandle(selectedPipeline, inputHandles, secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = findPossibleNodesToConnect(
          secondPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.InputNodeToInputModule:
      secondModuleNode = nodes.filter(node => node.id == secondGnum);
      if (!secondModuleNode || secondModuleNode.length === 0) {
        console.log("Module node not found:", edge);
      }
      inputHandles = secondModuleNode
        .map(node => node.data.inputHandles)
        .flat();
      if (inputHandles.includes(secondPart)) {
        return edge;
      } else {
        possibleMatchSecondPart = getNewNodeIgnoreOrderProcessRev(
          secondPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
        if (possibleMatchSecondPart) {
          const newEdge = mapEdge(
            firstPart,
            possibleMatchSecondPart,
            firstSplit,
            possibleMatchSecondPart.split("-"),
            firstChunk,
            firstCharOfFirstPart,
            possibleMatchSecondPart.split("-")[1]
          );
          return newEdge;
        }
      }
      break;
    case EDGE_TYPES.OutputNodeToOutputModule:
      secondModuleNode = nodes.filter(node => node.id == secondGnum);
      if (!secondModuleNode || secondModuleNode.length === 0) {
        console.log("Module node not found:", edge);
      }
      outputHandles = secondModuleNode
        .map(node => node.data.outputHandles)
        .flat();
      if (outputHandles.includes(secondPart)) {
        return edge;
      } else {
        possibleMatchSecondPart = getNewNodeIgnoreOrderProcessRev(
          secondPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
        if (possibleMatchSecondPart) {
          const newEdge = mapEdge(
            firstPart,
            possibleMatchSecondPart,
            firstSplit,
            possibleMatchSecondPart.split("-"),
            firstChunk,
            firstCharOfFirstPart,
            possibleMatchSecondPart.split("-")[1]
          );
          return newEdge;
        }
      }
      break;
    case EDGE_TYPES.OutputProcessToInputModule:
      firstProcessNode = nodes.filter(node => node.id == firstGnum);
      secondModuleNode = nodes.filter(node => node.id == secondGnum);
      if (
        !firstProcessNode ||
        firstProcessNode.length === 0 ||
        !secondModuleNode ||
        secondModuleNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      outputHandles = firstProcessNode
        .map(node => node.data.outputHandles)
        .flat();
      inputHandles = secondModuleNode
        .map(node => node.data.inputHandles)
        .flat();

      if (allowHandle(selectedPipeline, outputHandles, firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = findPossibleNodesToConnect(
          firstPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (inputHandles.includes(secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = getNewNodeIgnoreOrderProcessRev(
          secondPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.InputProcessToOutputModule:
      firstProcessNode = nodes.filter(node => node.id == firstGnum);
      secondModuleNode = nodes.filter(node => node.id == secondGnum);
      if (
        !firstProcessNode ||
        firstProcessNode.length === 0 ||
        !secondModuleNode ||
        secondModuleNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      inputHandles = firstProcessNode
        .map(node => node.data.inputHandles)
        .flat();
      outputHandles = secondModuleNode
        .map(node => node.data.outputHandles)
        .flat();

      if (allowHandle(selectedPipeline, inputHandles, firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = findPossibleNodesToConnect(
          firstPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (outputHandles.includes(secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = getNewNodeIgnoreOrderProcessRev(
          secondPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.OutputModuleToInputProcess:
      secondProcessNode = nodes.filter(node => node.id == secondGnum);
      firstModuleNode = nodes.filter(node => node.id == firstGnum);
      if (
        !secondProcessNode ||
        secondProcessNode.length === 0 ||
        !firstModuleNode ||
        firstModuleNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      outputHandles = firstModuleNode
        .map(node => node.data.outputHandles)
        .flat();
      inputHandles = secondProcessNode
        .map(node => node.data.inputHandles)
        .flat();

      if (outputHandles.includes(firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = getNewNodeIgnoreOrderProcessRev(
          firstPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (allowHandle(selectedPipeline, inputHandles, secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = findPossibleNodesToConnect(
          secondPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }

      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.InputModuleToOutputProcess:
      firstModuleNode = nodes.filter(node => node.id === firstGnum);
      secondProcessNode = nodes.filter(node => node.id === secondGnum);
      if (
        !firstModuleNode ||
        firstModuleNode.length === 0 ||
        !secondProcessNode ||
        secondProcessNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      inputHandles = firstModuleNode.map(node => node.data.inputHandles).flat();
      outputHandles = secondProcessNode
        .map(node => node.data.outputHandles)
        .flat();

      if (inputHandles.includes(firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = getNewNodeIgnoreOrderProcessRev(
          firstPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (allowHandle(selectedPipeline, outputHandles, secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = findPossibleNodesToConnect(
          secondPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }

      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.OutputModuleToInputModule:
      firstModuleNode = nodes.filter(node => node.id === firstGnum);
      secondModuleNode = nodes.filter(node => node.id === secondGnum);
      if (
        !firstModuleNode ||
        firstModuleNode.length === 0 ||
        !secondModuleNode ||
        secondModuleNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      inputHandles = secondModuleNode
        .map(node => node.data.inputHandles)
        .flat();
      outputHandles = firstModuleNode
        .map(node => node.data.outputHandles)
        .flat();

      if (inputHandles.includes(secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = getNewNodeIgnoreOrderProcessRev(
          secondPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (outputHandles.includes(firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = getNewNodeIgnoreOrderProcessRev(
          firstPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    case EDGE_TYPES.InputModuleToOutputModule:
      firstModuleNode = nodes.filter(node => node.id === firstGnum);
      secondModuleNode = nodes.filter(node => node.id === secondGnum);
      if (
        !firstModuleNode ||
        firstModuleNode.length === 0 ||
        !secondModuleNode ||
        secondModuleNode.length === 0
      ) {
        console.log("Process node not found:", edge);
      }
      inputHandles = firstModuleNode.map(node => node.data.inputHandles).flat();
      outputHandles = secondModuleNode
        .map(node => node.data.outputHandles)
        .flat();

      if (inputHandles.includes(firstPart)) {
        possibleMatchFirstPart = firstPart;
      } else {
        possibleMatchFirstPart = getNewNodeIgnoreOrderProcessRev(
          firstPart,
          inputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (outputHandles.includes(secondPart)) {
        possibleMatchSecondPart = secondPart;
      } else {
        possibleMatchSecondPart = getNewNodeIgnoreOrderProcessRev(
          secondPart,
          outputHandles,
          validEdges,
          selectedPipeline
        );
      }
      if (possibleMatchFirstPart && possibleMatchSecondPart) {
        const newEdge = mapEdge(
          possibleMatchFirstPart,
          possibleMatchSecondPart,
          possibleMatchFirstPart.split("-"),
          possibleMatchSecondPart.split("-"),
          possibleMatchFirstPart.split("-").slice(0, 2).join("-"),
          possibleMatchFirstPart.split("-")[0],
          possibleMatchSecondPart.split("-")[1]
        );
        return newEdge;
      }
      break;
    default:
      console.log("Edge type not found:", edge);
      break;
  }
};

/**
 *  * This function is used to find the correct node based on the edge
 *  * It is used for process nodes
 * @param edge string "i-16325-0-6-4"
 * @param processNodes string[] ["i-16271-0-6-4", "i-16271-1-11-4"]
 * @param validEdges string []
 * @param selectedPipeline PipelineInterface - contains all process revisions
 * @returns string "i-16271-0-6-4"
 */
const findPossibleNodesToConnect = (
  edge,
  processNodes,
  validEdges,
  selectedPipeline
) => {
  const parameterIdForEdge = edge.split("-")?.[3];
  const gNum = edge.split("-")?.[4];
  const indexForEdge = edge.split("-")?.[2];
  const hasProcessChanged =
    selectedPipeline?.changedProcessData?.[`tooltipsDiff_${gNum}`];

  // we can only allowed to recover based on the parameterId if there is only one node with the same parameterId
  // if there are multiple nodes with the same parameterId, we need to check if the process version is changed
  const nodesWithSameParameterId = processNodes.filter(
    node => node.split("-")?.[3] === parameterIdForEdge
  );
  if (nodesWithSameParameterId.length > 1) {
    const edgeType = edge.split("-")?.[0];
    if (hasProcessChanged) {
      console.log("nodesWithSameParameterId found", nodesWithSameParameterId);
      const possibleIndexes = nodesWithSameParameterId.map(
        node => node.split("-")[2]
      );
      if (
        !selectedPipeline.changedProcessData ||
        !selectedPipeline.changedProcessData[`tooltipsDiff_${gNum}`]
      ) {
        console.log("No changed process data found", edge);
        return null;
      }
      if (edgeType === "i") {
        const { oldInputTooltips, newInputTooltips } =
          selectedPipeline.changedProcessData[`tooltipsDiff_${gNum}`];

        const oldInputTooltip = oldInputTooltips[indexForEdge];
        for (const possibleIndex of possibleIndexes) {
          const newInputTooltip = newInputTooltips[possibleIndex];
          if (
            newInputTooltip &&
            oldInputTooltip &&
            newInputTooltip.name === oldInputTooltip.name &&
            newInputTooltip.identifier === oldInputTooltip.identifier &&
            newInputTooltip.qualifier === oldInputTooltip.qualifier &&
            newInputTooltip.fileType === oldInputTooltip.fileType
          ) {
            const newNode = nodesWithSameParameterId.find(
              node =>
                Number(node.split("-")[2]) === Number(possibleIndex) &&
                !validEdges.find(e => e?.id?.includes(node))
            );
            if (newNode) {
              console.log("newNode found", newNode, validEdges);
              return newNode;
            }
          }
        }
      } else if (edgeType === "o") {
        const { oldOutputTooltips, newOutputTooltips } =
          selectedPipeline.changedProcessData[`tooltipsDiff_${gNum}`];
        const oldOutputTooltip = oldOutputTooltips[indexForEdge];
        for (const possibleIndex of possibleIndexes) {
          const newOutputTooltip = newOutputTooltips[possibleIndex];
          if (
            newOutputTooltip &&
            oldOutputTooltip &&
            newOutputTooltip.name === oldOutputTooltip.name &&
            newOutputTooltip.identifier === oldOutputTooltip.identifier &&
            newOutputTooltip.qualifier === oldOutputTooltip.qualifier &&
            newOutputTooltip.fileType === oldOutputTooltip.fileType
          ) {
            const newNode = nodesWithSameParameterId.find(
              node =>
                Number(node.split("-")[2]) === Number(possibleIndex) &&
                !validEdges.find(e => e?.id?.includes(node))
            );
            if (newNode) {
              console.log("newNode found", newNode);
              return newNode;
            }
          }
        }
      }
    }
    console.log("single node not found", edge);
    return null;
  }

  // find the process node that matches with parameterIdForEdge and parameterIdForProcesses
  const processNode = processNodes.find(
    node =>
      node.split("-")?.[3] === parameterIdForEdge &&
      !validEdges.find(e => e?.id?.includes(node))
  );
  return processNode;
};

/**
 *  * This function is used to find the correct node based on the edge
 *  * It is used for module nodes
 * @param edge string "pi-16285-0-42068-5"
 * @param processNodes string[] ["pi-16285-0-42065-5"]
 * @param validEdges  string []
 */
const getNewNodeIgnoreOrderProcessRev = (
  edge,
  processNodes,
  validEdges,
  selectedPipeline = {}
) => {
  // edge format: "p{i/o}-{processId}-{orderIndex}-{parameterId}-{gnum}"
  const edgeType = edge.split("-")[0];
  const edgeOrder = edge.split("-")[2];

  const patt = /(.*)-(.*)-(.*)-(.*)-(.*)/;
  const moduleNodeRegEx = new RegExp(
    edge.replace(patt, "$1-" + "(.*)" + "(.*)" + "-$4-$5"),
    "g"
  );
  const newNodes = processNodes.filter(
    node =>
      node.match(moduleNodeRegEx) &&
      !validEdges.find(e => e?.id?.includes(node))
  );

  if (newNodes.length === 0) {
    console.log("No possible node found:", edge);
    return null;
  } else if (newNodes.length === 1) {
    return newNodes[0];
  } else {
    console.log("More than one possible node found:", edge, newNodes);
    const possibleIndexes = newNodes.map(node => node.split("-")[2]);
    const oldModuleData = selectedPipeline[`oldModuleData`];
    const currentModuleData = selectedPipeline[`currentModuleData`];
    if (
      !oldModuleData ||
      !oldModuleData.oldOutputTooltips ||
      !currentModuleData ||
      !currentModuleData.outputTooltips
    ) {
      return null;
    }
    const oldOutputTooltips = oldModuleData.oldOutputTooltips;
    const outputTooltips = currentModuleData.outputTooltips;
    const oldInputTooltips = oldModuleData.oldInputTooltips;
    const inputTooltips = currentModuleData.inputTooltips;
    if (edgeType === "pi") {
      const oldInputTooltip = oldInputTooltips[edgeOrder];
      // new tooltip should be the one that matches with the old tooltip
      for (const possibleIndex of possibleIndexes) {
        const newInputTooltip = inputTooltips[possibleIndex];
        if (
          newInputTooltip &&
          oldInputTooltip &&
          newInputTooltip.fileType === oldInputTooltip.fileType &&
          newInputTooltip.qualifier === oldInputTooltip.qualifier &&
          newInputTooltip.process === oldInputTooltip.process &&
          newInputTooltip.identifier === oldInputTooltip.identifier &&
          newInputTooltip.name === oldInputTooltip.name
        ) {
          console.log("found the correct node:", possibleIndex, newNodes);
          const newNode = newNodes.find(
            node => Number(node.split("-")[2]) === Number(possibleIndex)
          );
          return newNode;
        }
      }
      return null;
    } else if (edgeType === "po") {
      const oldOutputTooltip = oldOutputTooltips[edgeOrder];
      // new tooltip should be the one that matches with the old tooltip
      for (const possibleIndex of possibleIndexes) {
        const newOutputTooltip = outputTooltips[possibleIndex];
        if (
          newOutputTooltip &&
          oldOutputTooltip &&
          newOutputTooltip.fileType === oldOutputTooltip.fileType &&
          newOutputTooltip.qualifier === oldOutputTooltip.qualifier &&
          newOutputTooltip.process === oldOutputTooltip.process &&
          newOutputTooltip.identifier === oldOutputTooltip.identifier &&
          newOutputTooltip.name === oldOutputTooltip.name
        ) {
          console.log("found the correct node:", possibleIndex, newNodes);
          const newNode = newNodes.find(
            node => Number(node.split("-")[2]) === Number(possibleIndex)
          );
          return newNode;
        }
      }
      return null;
    } else {
      console.log("No old module data found:", edge);
      return null;
    }
  }
};

/**
 *  * This function maps the edges and returns the valid edges
 * @param edges []
 * @param nodes []
 * @returns EdgeInterface[]
 */
const validateEdges = (edges, nodes, selectedPipeline): EdgeInterface[] => {
  const validEdges: EdgeInterface[] = [];

  for (const edge of edges) {
    const { source, target } = edge;
    const sourceNode = nodes.find(node => node.id === source);
    const targetNode = nodes.find(node => node.id === target);

    if (!sourceNode || !targetNode) {
      console.log("Source or target node not found:", edge);
    } else {
      const edgeType = checkEdgeType(edge.id);
      const newEdge = checkIsEdgeExist(
        edge,
        nodes,
        edgeType,
        validEdges,
        selectedPipeline
      );
      if (newEdge) {
        validEdges.push(newEdge);
      } else {
        console.log("Edge not found:", edge);
      }
    }
  }
  delete selectedPipeline[`changedProcessData`];
  delete selectedPipeline[`oldModuleData`];
  delete selectedPipeline[`currentModuleData`];
  return validEdges;
};

export const divideSemiCircle = (
  type: string = "target",
  radius: number,
  numCircles: number
): Array<{ x: number; y: number }> => {
  // The angle between each circle (in radians)
  const angleBetweenCircles = Math.PI / (numCircles + 1);
  // Array to hold the position of each red circle
  const circlePositions: Array<{ x: number; y: number }> = [];
  const isSource = type === "source";

  for (let i = 1; i <= numCircles; i++) {
    // Calculate the angle for the current circle
    const angle = angleBetweenCircles * i;
    // Calculate the x and y coordinates for the current circle
    const x = isSource ? radius * Math.sin(angle) : -radius * Math.sin(angle); // negative for left side of circle, positive for right side
    const y = radius * Math.cos(angle);
    // Add the position to the array
    circlePositions.push({ x, y });
  }

  return circlePositions;
};

/**
 * Processes a set of module edges and extracts unique input (pi) and output (po) node identifiers.
 *
 * Given a collection of edges, each represented as a string containing source and target node details,
 * this function:
 * 1. Categorizes edges into "input process" or "output process" based on the presence of "inPro" or "outPro".
 * 2. Depending on whether the edge represents input or output, and whether its source or target begins
 *    with "i" (for input) or "o" (for output), it determines a "key" node and a "value" node to associate.
 * 3. Stores these associations in `inNodes` or `outNodes` mappings.
 * 4. Finally, it formats these mappings into unique node identifiers of the form:
 *    - For inputs: `pi-[processId]-[order]-[parameterId]-[gNum]`
 *    - For outputs: `po-[processId]-[order]-[parameterId]-[gNum]`
 *
 * @param {Object} param0
 *    - edges: an array of edges, each represented as a string (e.g., "i-..._o-...").
 * @param {string | number} gNum - A numeric or string identifier appended to output nodes.
 * @param {any} _pipelineId - An unused parameter, kept for function signature consistency.
 * @returns {edges:string[], matchingNodes:Map<string,string[]>}
 * - edges: An array of formatted unique node identifiers.
 * - matchingNodes: A map of unique node identifiers to their associated input/output nodes.
 *  key: `pi-[processId]-[order]-[parameterId]-[gNum]` or `po-[processId]-[order]-[parameterId]-[gNum]`
 *  value: An array of input/output nodes associated with the key.
 */
export const processNewModuleEdges = ({ edges }, gNum, _pipelineId): any => {
  const processingObj: any = {
    edOrg: edges ?? [],
    inNodes: new Map<string, string[]>(), // Mapping of input nodes to "input parameters"
    outNodes: new Map<string, string[]>(), // Mapping of output nodes to "output parameters"
  };
  const matchingNodes = new Map<string, string[]>();

  // currently we don't support green circles to be connected to multiple output handles. so outNodes will have only one value in array format. (to make it similar to inNodes)

  // inNodes[ we will keep inProID as key] = [connected input handles as value]
  // outNodes[ we will keep outProID as key] = [connected output handles as value]

  // Helper function to insert a node into a map
  const insertNode = (
    map: Map<string, string[]>,
    key: string,
    value: string
  ) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(value);
  };

  edges?.forEach(edge => {
    const [source, target] = edge.split("_");
    const isInputProcess = edge.includes("inPro");
    const isOutputProcess = edge.includes("outPro");

    if (isInputProcess) {
      //i-50-0-46-6_o-inPro-1-46-7
      // WE DON't Expect source[0] == "i") to happen since we write inPro first
      // however we will keep this check for now for consistency in 1.0
      const key = source.startsWith("i") ? target : source;
      const value = source.startsWith("i") ? source : target;
      insertNode(processingObj.inNodes, key, value);
    } else if (isOutputProcess) {
      //if they connected to same "input parameter"
      // o-inPro-1-46-7_i-50-0-46-6
      const key = source.startsWith("o") ? target : source;
      const value = source.startsWith("o") ? source : target;
      insertNode(processingObj.outNodes, key, value);
    }
  });

  const formatNodes = (
    nodeMap: Map<string, string[]>,
    prefix: string,
    gNum: string | number
  ) => {
    let order = 0;
    for (const [, nodes] of nodeMap) {
      // In 1.0 we used last Node's proId and paramId to create the unique node id
      const lastNode = nodes[nodes.length - 1];
      const proId = lastNode.split("-")[1];
      const paramId = lastNode.split("-")[3];
      matchingNodes.set(
        `${prefix}-${proId}-${order}-${paramId}-${gNum}`,
        nodes
      );
      order++;
    }
  };

  formatNodes(processingObj.inNodes, "pi", gNum);
  formatNodes(processingObj.outNodes, "po", gNum);

  const newEdges = [...matchingNodes.keys()].flat();

  return {
    edges: newEdges,
    matchingNodes,
  };
};

export const cleanAndParse = (value): any =>
  typeof value === "string" ? JSON.parse(value.replace(/'/g, '"')) : value;

// I will clean the types up later. this is so I can go fast and iterate
export const cleanAndParsePipeline = (data): any => {
  return {
    ...data,
    nodes: cleanAndParse(data.nodes),
    edges: cleanAndParse(data.edges),
    mainG: cleanAndParse(data.mainG),
  };
};

export const cleanAndParseProcess = (data): any => {
  return {
    ...data,
    inputs: data?.inputs?.map(input => {
      return { ...input, sname: input?.sname };
    }),
    outputs: data?.outputs?.map(output => {
      return { ...output, sname: output?.sname };
    }),
    script: data?.script,
    script_header: data?.script_header,
    script_footer: data?.script_footer,
    script_mode_header: LANGUAGE_MODES.GROOVY,
    script_mode_footer: LANGUAGE_MODES.GROOVY,
  };
};

/**
 * * This function takes the process data and formats it to send to the backend
 * * Call it before saving/creating a process, overwriting a process and revising a process
 */
export const packProcessToSave = (data): any => {
  const defaultFirstRevisionComment = "Initial revision";
  // In the future, the backend will return everything how we need it to
  const payload = {
    summary: data?.description,
    inputParameters: data?.inputParameters.map(input => {
      return {
        parameterId: input.parameterId,
        displayName: input.displayName ?? "",
        operator: input.operator,
        operatorContent: input.operatorContent,
        optional: input.optional,
        test: input.test,
        ...(input?.id && { id: input.id }),
      };
    }),
    outputParameters: data?.outputParameters.map(output => {
      return {
        parameterId: output.parameterId,
        displayName: output.displayName,
        operator: output.operator,
        operatorContent: output.operatorContent,
        optional: output.optional,
        // Only outputs carry a publish pattern, and only when we actually loaded or edited
        // one. Omitting the key tells the backend to leave the stored value alone, which is
        // what keeps an imported process's pattern intact.
        ...(output?.regEx !== undefined && { regEx: output.regEx }),
        ...(output?.id && { id: output.id }),
      };
    }),
    script: {
      body: data?.script.body,
      header: data?.script.header,
      footer: data?.script.footer,
      language:
        data.script.language === LANGUAGE_MODES.SHELL
          ? "sh"
          : data.script.language,
    },
    permissionSettings: data.permissionSettings,
    ...(data?.menuGroupId && { menuGroupId: data.menuGroupId }),
  };
  return {
    ...payload,
    name: data.name,
    ...(data?.isNewProcess
      ? { revisionComment: defaultFirstRevisionComment }
      : { revisionComment: data.revisionComment }),
  };
};

type ProcessPortSide = "inputParameters" | "outputParameters";

/**
 * Removes one port row from a process draft.
 *
 * There is no endpoint that deletes a port: the row is left out of the next overwrite save, and
 * the backend deletes every stored port the save does not carry. The backend refuses a save whose
 * side is empty while ports are stored there, unless the save says that is intended, so when the
 * last row on a side goes the draft records that the user emptied it.
 */
export const removeProcessPortRow = (
  draft,
  side: ProcessPortSide,
  index: number
): any => {
  const rows = draft?.[side] ?? [];
  const remaining = [...rows.slice(0, index), ...rows.slice(index + 1)];
  return {
    ...draft,
    [side]: remaining,
    ...(remaining.length === 0 && {
      removedAllPorts: { ...draft?.removedAllPorts, [side]: true },
    }),
  };
};

/**
 * Packs a draft for an overwrite save (PUT), which is the only save that deletes ports.
 *
 * A side is declared intentionally empty only when the user emptied it and it is still empty.
 * Create and revise go through `packProcessToSave` alone: their schemas reject unknown keys.
 */
export const packProcessToOverwrite = (data): any => {
  const payload = packProcessToSave(data);
  return {
    ...payload,
    ...(data?.removedAllPorts?.inputParameters &&
      payload.inputParameters?.length === 0 && {
        removeAllInputParameters: true,
      }),
    ...(data?.removedAllPorts?.outputParameters &&
      payload.outputParameters?.length === 0 && {
        removeAllOutputParameters: true,
      }),
  };
};

export const formatPipelineProcessData = (data = {}): any => {
  const prefix = "process_";
  return Object.keys(data)
    .filter(key => key.startsWith(prefix)) // Filter keys that start with 'process_'
    .map(key => data[key]); // Map these keys to their corresponding objects
};

export const findHighestGNum = (nodes = {}): number => {
  return Object.keys(nodes).reduce((max, key) => {
    const currentNumber = parseInt(key.split("-")?.[1], 10);
    return Math.max(max, currentNumber);
  }, 0);
};

export const filterNodesByConditions = (nodes, conditions) => {
  return nodes.reduce((acc, node) => {
    const matchesConditions = conditions(node);
    const filteredChildren = node.children
      ? filterNodesByConditions(node.children, conditions)
      : [];
    if (matchesConditions || filteredChildren.length > 0) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, []);
};

export const filterBySearchTerm = (data, searchTerm): any => {
  const searchTermLower = searchTerm.toLowerCase();

  function filterRecursive(nodes): any {
    return nodes.reduce((acc, node) => {
      const match = node.name?.toLowerCase().includes(searchTermLower);
      const children = node.children ? filterRecursive(node.children) : [];

      if (match || children.length > 0) {
        acc.push({
          ...node,
          children:
            node.nodeType === "category" && match ? node.children : children,
        });
      }

      return acc;
    }, []);
  }

  return filterRecursive(data);
};

export const processTree = tree => {
  const combineLineageItems = items => {
    const lineageMap = new Map();

    items.forEach(item => {
      if (item.lineageId !== undefined) {
        const existingItem = lineageMap.get(item.lineageId);
        if (!existingItem || item.revId > existingItem.revId) {
          lineageMap.set(item.lineageId, { ...item });
        }
      } else {
        lineageMap.set(item.id, { ...item });
      }
    });

    return Array.from(lineageMap.values());
  };

  const processNode = node => {
    if (node.children && node.children.length > 0) {
      node.children = combineLineageItems(node.children.map(processNode));
    }
    return node;
  };

  return tree.map(processNode);
};

export const getInputParamLabels = value => {
  switch (value) {
    case "name":
      return "Name";
    case "defVal":
      return "Define Default Value";
    case "dropDown":
      return "Dropdown Options";
    case "showSett":
      return "Show Settings";
    case "inLabelOpt":
      return "Label";
    case "inDescOpt":
      return "Description";
  }
};

/**
 * Reads one side's ports off a loaded process, refusing to invent them.
 *
 * An absent key means the response did not carry the ports, which is not the same as the process
 * having none. Coercing it to [] is how a partial load became a draft with no ports, and then a
 * save that asked the backend to delete every parameter the process had (#329, #387). A process
 * that came back from the API always carries both arrays, so an absent one is a failed load and
 * there is no safe draft to build from it.
 *
 * A process with no id does not exist yet, so it genuinely has no ports and [] is correct.
 */
const readProcessPorts = (
  ports: unknown,
  side: "input" | "output",
  processId: unknown,
  map: (io: any) => any
): any[] => {
  if (Array.isArray(ports)) {
    return ports.map(map);
  }
  if (processId === undefined || processId === null) {
    return [];
  }
  throw new Error(
    `Process ${processId} could not be loaded: the response did not carry its ${side} parameters. ` +
      `Editing it now would risk saving over them, so no draft was created.`
  );
};

export const formatProcessDataToNewSchema = schema => {
  // In the future, the backend will return everything how we need it to
  const mapInputOutput = io => ({
    parameterId: io?.parameter_id,
    displayName: io?.sname ?? "",
    originalName: io?.name ?? "",
    operator: io?.operator ?? "",
    operatorContent: io?.closure ?? "",
    optional: io?.optional === "true",
    test: io?.test ?? "",
    regEx: io?.reg_ex ?? "",
    fileType: io?.file_type ?? "",
    qualifier: io?.qualifier ?? "",
    id: io?.id ?? "",
  });

  let language = LANGUAGE_MODES.GROOVY;
  if (schema?.script_mode === "sh") {
    language = LANGUAGE_MODES.SHELL;
  } else if (schema?.script_mode) {
    language = schema.script_mode;
  }

  return {
    name: schema?.name,
    processId: schema?.id,
    createdBy: schema?.username,
    lastModified: schema?.last_modified || schema?.date_modified,
    createdAt: schema?.date_created,
    description: schema?.summary,
    menuGroupId: schema?.process_group_id,
    menuGroupName: schema?.process_group_name,
    inputParameters: readProcessPorts(
      schema?.inputs,
      "input",
      schema?.id,
      mapInputOutput
    ),
    outputParameters: readProcessPorts(
      schema?.outputs,
      "output",
      schema?.id,
      mapInputOutput
    ),
    revisionComment: schema?.rev_comment,
    revisionId: schema?.rev_id,
    script: {
      body: schema?.script ?? "",
      header: schema?.script_header ?? "",
      footer: schema?.script_footer ?? "",
      language,
    },
    permissionSettings: {
      viewPermissions: schema?.perms ?? null,
      sharedGroupId: schema?.group_id ?? null,
      writeGroupIds:
        schema?.write_group_id && schema.write_group_id.length > 0
          ? schema?.write_group_id
              ?.split(",")
              .map(Number)
              .filter(id => id !== 0)
          : [],
    },
  };
};

export const createEmptyPipeline = data => {
  return {
    name: data?.name ?? "",
    summary: data?.summary ?? "",
    description: "",
    edges: [],
    mainG: [0, 0, 1],
    nodes: {},
    scriptPipeConfig:
      "\n" +
      "//~@:~\n" +
      `@~:"nextflow.config"\n` +
      "//~@:~\n" +
      "nextflow.enable.dsl=2\n" +
      `$INITIAL_RUN_TYPE="gzip"\n` +
      "    \n" +
      "//* autofill\n" +
      `if ($HOSTNAME == "default"){\n` +
      `    $DOCKER_IMAGE = "quay.io/viascientific/pipeline_base_image:1.0"\n` +
      "}\n" +
      "//* autofill\n" +
      "//~@:~\n",
    scriptPipeHeader: "",
    scriptPipeFooter: "",
    scriptModeHeader: LANGUAGE_MODES.GROOVY,
    scriptModeFooter: LANGUAGE_MODES.GROOVY,
    pipelineGroupId: data?.menuGroup,
    pipelineType: PIPELINE_TYPES.VIA_FOUNDRY,
    publishWebDir: "",
    publishWebAppDir: "",
    publishDMetaDir: "",
    permission: 3,
    version: "1.0.0",
    revComment: "Initial Version",
  };
};

export const createEmptyProcess = () => {
  return {
    isNewProcess: true,
    name: "",
    processId: null,
    createdBy: "",
    lastModified: "",
    createdAt: "",
    description: "",
    menuGroupId: null,
    menuGroupName: "",
    inputParameters: [],
    outputParameters: [],
    script: {
      body: "",
      header: "",
      footer: "",
      language: LANGUAGE_MODES.GROOVY,
    },
    permissionSettings: {
      viewPermissions: 3,
      writeGroupIds: [],
    },
    type: MenuGroupType.PROCESS,
  };
};

/** The functions below are related to updating / managing edges onConnect and onDisconnect **/

const generateIOParameterHandleOnDisconnect = (id, type) => {
  if (type === NODE_TYPES.InputNode) {
    const numberToReplace = id?.split("-")?.[3];
    const textToReplaceWith = "inPara";
    return id?.replace(numberToReplace, textToReplaceWith);
  } else if (type === NODE_TYPES.OutputNode) {
    const numberToReplace = id?.split("-")?.[3];
    const textToReplaceWith = "outPara";
    return id?.replace(numberToReplace, textToReplaceWith);
  }
};

export const getIOParameterID = edgeId => {
  // Split the string by underscore
  const parts = edgeId?.split("_") || [];

  // Find the part that contains 'inPro' or 'outPro'
  const targetPart =
    parts.find(part => part.includes("inPro") || part.includes("outPro")) || "";

  // Determine if we are dealing with an input or output parameter
  const type = targetPart.includes("inPro") ? "inputNode" : "outputNode";

  // Generate the new id since we are disconnected now
  const newId = generateIOParameterHandleOnDisconnect(targetPart, type);

  // Split the target part by hyphen and return the 4th index
  const targetParts = targetPart.split("-");
  return {
    nodeId: targetParts[4],
    newId,
    parameterId: parseInt(targetParts[3]),
    type,
  };
};

// Ids can be saved in differing order, so split the strings, sort alphabetically and then rejoin to compare
export const isMatchingId = (
  current: string = "",
  existing: string = ""
): boolean => {
  return (
    current.split("_").sort().join("_") === existing.split("_").sort().join("_")
  );
};

const connectionExists = (edge, edges) => {
  return edges?.some(
    el =>
      (el.sourceHandle === edge?.sourceHandle ||
        (!el.sourceHandle && !edge?.sourceHandle)) &&
      (el.targetHandle === edge?.targetHandle ||
        (!el.targetHandle && !edge?.targetHandle))
  );
};

const getEdgeId = edge => {
  const sourceHandle = edge?.sourceHandle ?? "";
  const targetHandle = edge?.targetHandle ?? "";
  return `${sourceHandle}_${targetHandle}`;
};

export const addEdge = (edgeParams, edges) => {
  if (!edgeParams.source || !edgeParams.target) {
    return edges;
  }

  const edge = {
    ...edgeParams,
    id: getEdgeId(edgeParams),
  };

  if (connectionExists(edge, edges)) {
    return edges?.map(e => (isMatchingId(edge.id, e.id) ? edge : e));
  }

  const formatted = { edges: [edge.id] };
  const result = formatEdgesData(formatted);

  return edges
    ?.filter(e => e.id !== edge.sourceHandle)
    .filter(e => e.id !== edge.targetHandle)
    .concat(result);
};

export const packScriptConfig = scripts => {
  const separator = "\n//~@:~\n";
  let scriptPipeConfig = "";
  let scriptPipeHeader = "";
  let scriptPipeFooter = "";

  scripts?.forEach(item => {
    if (item?.filename === SCRIPT_PIPE_HEADER) {
      scriptPipeHeader = item?.text;
    } else if (item?.filename === SCRIPT_PIPE_FOOTER) {
      scriptPipeFooter = item?.text;
    } else {
      // item.text = item.text
      //   .replace(/\r\n/g, "\n")
      //   .replace(/\r/g, "\n");
      scriptPipeConfig += `${separator}@~:"${item?.filename}"${separator}${item?.text}${separator}`;
    }
  });

  return {
    scriptPipeConfig,
    scriptPipeHeader,
    scriptPipeFooter,
  };
};

/**
 * * This function is used to format the pipeline draft data before sending it to backend
 *  * Call it before saving pipeline, duplicating pipeline, or revising pipeline
 * @param draft PipelineDraftData
 * @param config ScriptPipeConfig
 * @param action PipelineOperations
 * @returns PipelineDraftData
 */
export const packDraftForSave = (
  draft: PipelineDraftData,
  config: ScriptPipeConfig,
  action: string = PipelineOperations.Revise
): Omit<PipelineDraftData, "dateCreated" | "dateModified"> => {
  const data = { ...draft };
  const isExternal = isPipelineExternal(data.pipelineType);
  const { scriptPipeConfig, scriptPipeHeader, scriptPipeFooter } = config;
  const cleanedNodes = Object.fromEntries(
    Object.entries(data.nodes).map(([key, node]) => {
      let formattedNodeSettings = {};
      const {
        inputHandles,
        outputHandles,
        type,
        tooltips,
        param,
        nodeSettings,
        ...rest
      } = node;
      let parsedParam = param.toString();
      if (type === NODE_TYPES.ModuleNode) {
        parsedParam = `p${param}`;
      }
      if (type === NODE_TYPES.ModelNode) {
        parsedParam = `m${param}`;
      }
      if (type === NODE_TYPES.SkillNode) {
        parsedParam = `s${param}`;
      }

      if (
        nodeSettings &&
        nodeSettings.pubWebApp &&
        (nodeSettings.pubWebApp as OutputParameterPayload["pubWebApp"])?.app
      ) {
        formattedNodeSettings = {
          ...nodeSettings,
          pubWebApp: {
            ...nodeSettings.pubWebApp,
            app: (
              nodeSettings.pubWebApp as OutputParameterPayload["pubWebApp"]
            )?.app!.map(app => app.toString()),
          },
        };
      } else {
        formattedNodeSettings = nodeSettings;
      }

      return [
        key,
        { ...rest, param: parsedParam, nodeSettings: formattedNodeSettings },
      ];
    })
  );
  const cleanedEdges = data?.edges?.map(edge => {
    const parts = edge.split("_");
    const first = parts.find(
      part => part.includes("o-inPro") || part.includes("i-outPro")
    );
    const second = parts.find(
      part => !part.includes("o-inPro") && !part.includes("i-outPro")
    );
    return first && second ? `${first}_${second}` : edge;
  });

  const {
    id,
    isEditingAllowed,
    isAdmin,
    isOwner,
    dateCreated,
    dateModified,
    writeGroupIds,
    rootId,
    ...revised
  } = data;

  // Compress large pipeline content fields to prevent Apache firewall rejection
  const finalScriptPipeConfig = isExternal
    ? data.scriptPipeConfig
    : scriptPipeConfig;
  const compressedScriptPipeConfig = compressFieldIfNeeded(
    finalScriptPipeConfig
  );

  const finalExtractedPipeScript = isExternal ? scriptPipeConfig : undefined;
  const compressedExtractedPipeScript = compressFieldIfNeeded(
    finalExtractedPipeScript
  );

  const payload: Partial<PipelineDraftData> = {
    id,
    ...revised,
    ...(action === PipelineOperations.Save && { id }),
    nodes: cleanedNodes,
    edges: cleanedEdges,
    scriptPipeConfig: compressedScriptPipeConfig.data ?? undefined,
    ...(isExternal && {
      extractedPipeScript: compressedExtractedPipeScript.data ?? undefined,
    }),
    scriptPipeHeader,
    scriptPipeFooter,
    ...(writeGroupIds && writeGroupIds.length > 0 && { writeGroupIds }),
  };

  // Remove any top-level keys that have null values
  Object.keys(payload).forEach(key => {
    if (payload[key] === null) {
      delete payload[key];
    }
  });

  return payload as Omit<
    PipelineDraftData,
    "id" | "dateCreated" | "dateModified"
  >;
};

export const removeEmptyStrings = (item = {}) => {
  return Object.fromEntries(
    Object.entries(item).filter(([, value]) => value !== "")
  );
};

/**
 * Determines the CSS class for a handle based on hover state and connection status.
 * This function controls the visual feedback (pulse animations, sizing, opacity) for handles
 * when users hover over nodes or handles to create connections.
 *
 * @param {Object} data - Node data containing hover state and type information
 * @param {string} data.hovered - File type of the currently hovered handle
 * @param {string} data.hoverHandleType - Type of the hovered handle (source/target)
 * @param {string} data.hoverHandle - ID of the currently hovered handle
 * @param {string} data.type - Type of the current node (ProcessNode, ModuleNode, etc.)
 * @param {boolean} data.isDragging - Whether a connection is being dragged
 * @param {string} data.hoverNode - ID of the currently hovered node
 * @param {string} data.hoverNodeType - Type of the hovered node
 * @param {boolean} data.hoverNodeConnected - Whether the hovered node/handle is connected
 *
 * @param {string} file - File type of the current handle being evaluated
 * @param {string} handle - ID of the current handle being evaluated
 * @param {string} handleType - Type of the current handle ("source" or "target")
 * @param {string|null} id - ID of the current node
 * @param {boolean} isConnected - Whether THIS specific handle is connected to something
 *
 * @returns {string} CSS class string (e.g., "h-6 w-6 pulse-ProcessNode-source" or "h-4 w-4 opacity-50")
 *
 * @description
 * The function determines handle appearance based on these scenarios:
 * 1. **Pulsing (h-6 w-6 pulse-*)**: Handle is available for connection
 *    - Happens when hovering over an unconnected node/handle and this handle is also unconnected
 *    - File types must match (if applicable)
 *
 * 2. **Shrinking (h-4 w-4 opacity-50)**: Handle is not available for connection
 *    - Already connected handles when they can't accept more connections
 *    - Incompatible handle types (e.g., source with source)
 *    - Same node's handles (can't connect to itself)
 *
 * 3. **Normal (h-5 w-5)**: Default state when not hovering
 *
 * Key Logic:
 * - Only unconnected handles pulse when hovering over unconnected nodes
 * - Connected handles don't pulse (to avoid suggesting new connections where they can't be made)
 * - File type matching ensures compatible connections
 * - Prevents self-connections and invalid node type combinations
 */
export const getHandleClass = (
  data,
  file,
  handle,
  handleType,
  id = null,
  isConnected = false
) => {
  const {
    hovered,
    hoverHandleType,
    hoverHandle,
    type: nodeType,
    isDragging,
    hoverNode,
    hoverNodeType,
    hoverNodeConnected,
  } = data || {};
  const shouldEnlarge =
    (hovered && hovered === file && hoverHandleType !== handleType) ||
    hoverHandle === handle;
  const shouldShrink = hovered && hoverHandle !== handle;
  const isInputOutputCombo =
    (hoverNodeType === NODE_TYPES.InputNode &&
      nodeType === NODE_TYPES.OutputNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      nodeType === NODE_TYPES.InputNode);
  const isSameNode =
    (id === hoverNode &&
      hoverNodeType === NODE_TYPES.ProcessNode &&
      hoverHandleType === HandleTypes.Source &&
      handleType === HandleTypes.Target) ||
    (id === hoverNode &&
      hoverNodeType === NODE_TYPES.ProcessNode &&
      hoverHandleType === HandleTypes.Target &&
      handleType === HandleTypes.Source) ||
    (id === hoverNode &&
      hoverNodeType === NODE_TYPES.ModuleNode &&
      hoverHandleType === HandleTypes.Source &&
      handleType === HandleTypes.Target) ||
    (id === hoverNode &&
      hoverNodeType === NODE_TYPES.ModuleNode &&
      hoverHandleType === HandleTypes.Target &&
      handleType === HandleTypes.Source) ||
    (id === hoverNode &&
      hoverNodeType === NODE_TYPES.ModelNode &&
      hoverHandleType === HandleTypes.Source &&
      handleType === HandleTypes.Target) ||
    (id === hoverNode &&
      hoverNodeType === NODE_TYPES.ModelNode &&
      hoverHandleType === HandleTypes.Target &&
      handleType === HandleTypes.Source);
  // If you're hovering a process / module / model and the outputNode is connected, don't highlight anything as connectable
  if (
    (isConnected &&
      hoverNodeType === NODE_TYPES.ProcessNode &&
      nodeType === NODE_TYPES.OutputNode) ||
    (isConnected &&
      hoverNodeType === NODE_TYPES.ModuleNode &&
      nodeType === NODE_TYPES.OutputNode) ||
    (isConnected &&
      hoverNodeType === NODE_TYPES.ModelNode &&
      nodeType === NODE_TYPES.OutputNode)
  ) {
    return "h-4 w-4 opacity-50";
  }
  // If you're hovering an output and the outputNode is connected, don't highlight anything as connectable
  if (
    (hoverNodeConnected &&
      hoverNodeType === NODE_TYPES.OutputNode &&
      nodeType === NODE_TYPES.ProcessNode) ||
    (hoverNodeConnected &&
      hoverNodeType === NODE_TYPES.OutputNode &&
      nodeType === NODE_TYPES.ModuleNode) ||
    (hoverNodeConnected &&
      hoverNodeType === NODE_TYPES.OutputNode &&
      nodeType === NODE_TYPES.ModelNode)
  ) {
    return "h-4 w-4 opacity-50";
  }
  // Edge cases for outputNodes to solve the bugs that existed in Foundry 1.0
  if (
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Source &&
      !isConnected &&
      nodeType === NODE_TYPES.ProcessNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Source &&
      !isConnected &&
      nodeType === NODE_TYPES.ModuleNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Source &&
      !isConnected &&
      nodeType === NODE_TYPES.ModelNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Source &&
      !isConnected &&
      nodeType === NODE_TYPES.SkillNode)
  ) {
    return `h-6 w-6 pulse-${nodeType}-${handleType}`;
  } else if (
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Target &&
      nodeType === NODE_TYPES.ProcessNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Target &&
      nodeType === NODE_TYPES.ModuleNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Target &&
      nodeType === NODE_TYPES.ModelNode) ||
    (hoverNodeType === NODE_TYPES.OutputNode &&
      !hoverNodeConnected &&
      handleType === HandleTypes.Target &&
      nodeType === NODE_TYPES.SkillNode)
  ) {
    return "h-4 w-4 opacity-50";
  } else if (
    (hoverNodeType === NODE_TYPES.ProcessNode &&
      nodeType === NODE_TYPES.OutputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Source) ||
    (hoverNodeType === NODE_TYPES.ModuleNode &&
      nodeType === NODE_TYPES.OutputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Source) ||
    (hoverNodeType === NODE_TYPES.ModelNode &&
      nodeType === NODE_TYPES.OutputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Source) ||
    (hoverNodeType === NODE_TYPES.SkillNode &&
      nodeType === NODE_TYPES.OutputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Source)
  ) {
    return `h-6 w-6 pulse-${nodeType}-${handleType}`;
  } else if (
    hoverNodeType === NODE_TYPES.OutputNode &&
    !hoverNodeConnected &&
    nodeType === NODE_TYPES.OutputNode &&
    isConnected
  ) {
    return "h-4 w-4 opacity-50";
  }
  if (
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.ProcessNode &&
      handleType === HandleTypes.Target &&
      !isConnected) ||
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.ModuleNode &&
      handleType === HandleTypes.Target &&
      !isConnected) ||
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.ModelNode &&
      handleType === HandleTypes.Target &&
      !isConnected) ||
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.SkillNode &&
      handleType === HandleTypes.Target &&
      !isConnected)
  ) {
    return `h-6 w-6 pulse-${nodeType}-${handleType}`;
  } else if (
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.ProcessNode &&
      handleType === HandleTypes.Source) ||
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.ModuleNode &&
      handleType === HandleTypes.Source) ||
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.ModelNode &&
      handleType === HandleTypes.Source) ||
    (hoverNodeType === NODE_TYPES.InputNode &&
      !hoverNodeConnected &&
      nodeType === NODE_TYPES.SkillNode &&
      handleType === HandleTypes.Source)
  ) {
    return "h-4 w-4 opacity-50";
  } else if (
    (hoverNodeType === NODE_TYPES.ProcessNode &&
      nodeType === NODE_TYPES.InputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Target) ||
    (hoverNodeType === NODE_TYPES.ModuleNode &&
      nodeType === NODE_TYPES.InputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Target) ||
    (hoverNodeType === NODE_TYPES.ModelNode &&
      nodeType === NODE_TYPES.InputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Target) ||
    (hoverNodeType === NODE_TYPES.SkillNode &&
      nodeType === NODE_TYPES.InputNode &&
      !isConnected &&
      !hoverNodeConnected &&
      hoverHandleType === HandleTypes.Target)
  ) {
    return `h-6 w-6 pulse-${nodeType}-${handleType}`;
  } else if (
    hoverNodeType === NODE_TYPES.InputNode &&
    !hoverNodeConnected &&
    nodeType === NODE_TYPES.InputNode &&
    isConnected
  ) {
    return "h-4 w-4 opacity-50";
  }
  // Don't highlight source / target combos on the same node with multiple handles
  if (isSameNode) {
    return "h-4 w-4 opacity-50";
  }
  // Don't highlight an input / output node since they can't be connected
  if (isInputOutputCombo) {
    return "h-4 w-4 opacity-50";
  }
  // If dragging, maintain the styles. Enlarge if fileType matches, shrink if not and return to normal onMouseLeave
  if (isDragging) {
    if (shouldEnlarge) {
      return `h-6 w-6 pulse-${nodeType}-${handleType}`;
    }
    if (shouldShrink) {
      return "h-4 w-4 opacity-50";
    }
  } else {
    if (shouldEnlarge) {
      return `h-6 w-6 pulse-${nodeType}-${handleType}`;
    }
    if (shouldShrink) {
      return "h-4 w-4 opacity-50";
    }
  }
  return "h-5 w-5";
};

export const checkForOtherConnections = (edges, edgeId) => {
  const [sourceHandle, targetHandle] = (edgeId ?? "").split("_");
  // Filter the edges to find matching sourceHandle or targetHandle
  const matchingEdges =
    edges.filter(
      edge =>
        edge?.sourceHandle === sourceHandle ||
        edge?.targetHandle === targetHandle
    ) || [];
  // Check if the length of matchingEdges is greater than 1
  return matchingEdges.length > 1;
};

export const getPipelineTypeIcon = (type: string): ReactNode => {
  switch (type) {
    case PIPELINE_TYPES.NFCORE:
      return <img src={NFCoreLogo} className="mr-1 h-3" alt="NF-Core Logo" />;
    case PIPELINE_TYPES.VIA_FOUNDRY:
      return <img src={Logo} className="mr-1 h-3" alt="Foundry Connect logo" />;
    default:
      return <></>;
  }
};

// The PIPELINE_TYPES.VIA_FOUNDRY enum value must stay "ViaFoundry" (persisted
// pipeline_type discriminator / API contract) — this maps it to the current
// display name without changing the underlying value.
export const getPipelineTypeLabel = (type: string): string => {
  switch (type) {
    case PIPELINE_TYPES.VIA_FOUNDRY:
      return "Foundry Connect";
    default:
      return type;
  }
};

export const cleanProcessName = (proName?: string) => {
  if (proName) {
    proName = proName.trim();
    proName = proName.replace(/ /g, "_");
    proName = proName.replace(/-/g, "_");
    proName = proName.replace(/:/g, "_");
    proName = proName.replace(/,/g, "_");
    proName = proName.replace(/\$/g, "_");
    proName = proName.replace(/!/g, "_");
    proName = proName.replace(/</g, "_");
    proName = proName.replace(/>/g, "_");
    proName = proName.replace(/\?/g, "_");
    proName = proName.replace(/\(/g, "_");
    proName = proName.replace(/"/g, "_");
    proName = proName.replace(/'/g, "_");
    proName = proName.replace(/\./g, "_");
    proName = proName.replace(/\//g, "_");
    proName = proName.replace(/\\/g, "_");
    proName = proName.replace(/@/g, "_");
  }
  return proName;
};

/**
 * * This function is a modified version of the useUpdateNodeInternals hook from the library.
 * * It adds a promise wrapper around the original hook to ensure that the DOM update is complete before resolving the promise.
 */
export function useUpdateNodeInternalsAsync(): (
  id: string | string[]
) => Promise<void> {
  const updateNodeInternals = useUpdateNodeInternals(); // Get the hook from the library

  return (id: string | string[]) => {
    return new Promise<void>(resolve => {
      updateNodeInternals(id); // Call the original hook
      requestAnimationFrame(() => {
        // We know that the DOM update is done at this point
        resolve();
      });
    });
  };
}

/**
 *  This function is used to check if the output parameter has a connection
 *  If the output parameter has a connection, don't allow a new output node connection
 * @param edges Edge[]
 * @param newConnection Connection
 * @return boolean
 */
export const checkIfOutputParamHasOutputNodeConnection = (
  edges: Edge[],
  newConnection: Connection
) => {
  if (
    newConnection.sourceHandle?.startsWith("po") ||
    (newConnection.sourceHandle?.startsWith("o") &&
      !newConnection.sourceHandle?.startsWith("o-inPro"))
  ) {
    const outputParamConnections =
      edges?.filter(
        (edge: Edge) =>
          (edge?.sourceHandle === newConnection.sourceHandle &&
            edge?.targetHandle!.startsWith("i-outPro")) ||
          (edge?.targetHandle === newConnection?.targetHandle &&
            edge?.sourceHandle!.startsWith("i-outPro"))
      ) || [];

    if (
      outputParamConnections.length &&
      newConnection.targetHandle?.startsWith("i-outPro")
    ) {
      return true;
    }
  }
  return false;
};

/**
 *  This function is used to check if the input parameter has any connection
 *  If the input parameter has any connection, don't allow a new  connection
 * @param edges Edge[]
 * @param newConnection Connection
 * @return boolean
 */
export const checkIfInputParamHasAnyConnection = (
  edges: Edge[],
  newConnection: Connection
) => {
  if (
    newConnection.targetHandle?.startsWith("pi") ||
    (newConnection.targetHandle?.startsWith("i") &&
      !newConnection.targetHandle?.startsWith("i-outPro"))
  ) {
    const inputParamConnections =
      edges?.filter(
        (edge: Edge) => edge.targetHandle === newConnection.targetHandle
      ) || [];

    if (inputParamConnections.length) {
      return true;
    }
  }
  return false;
};

export enum SaveProcessMode {
  SAVE = "Save",
  SAVEANDLEAVE = "Save & Leave",
}

/**
 * This function filters out scripts that are empty folders
 * @param scripts AdvancedFilesInterface[]
 * @returns AdvancedFilesInterface[] excluding entries that represent empty folders
 */
export const filterEmptyFolders = (
  scripts: AdvancedFilesInterface[]
): AdvancedFilesInterface[] => {
  return scripts.filter(
    script => !(script?.filename?.endsWith("/") && script?.text === "")
  );
};

/**
 * Checks for duplicate labels among the given pipeline draft nodes.
 * @param nodes - A record of pipeline draft nodes
 * @returns An object containing arrays of duplicate labels for process nodes, module nodes, model nodes and skill nodes
 */
export const checkDuplicateLabels = (
  nodes: Record<string, PipelineDraftNode>
): Record<string, string[]> => {
  const duplicates = {
    processNode: [] as string[],
    moduleNode: [] as string[],
    modelNode: [] as string[],
    skillNode: [] as string[],
  };

  const processLabelMap = new Set<string>();
  const moduleLabelMap = new Set<string>();
  const modelLabelMap = new Set<string>();
  const skillLabelMap = new Set<string>();

  Object.values(nodes).forEach(node => {
    const { nodeName, type } = node;

    if (type === NODE_TYPES.ProcessNode) {
      if (
        processLabelMap.has(nodeName) &&
        !duplicates.processNode.includes(nodeName)
      ) {
        duplicates.processNode.push(nodeName);
      } else {
        processLabelMap.add(nodeName);
      }
    } else if (type === NODE_TYPES.ModuleNode) {
      if (
        moduleLabelMap.has(nodeName) &&
        !duplicates.moduleNode.includes(nodeName)
      ) {
        duplicates.moduleNode.push(nodeName);
      } else {
        moduleLabelMap.add(nodeName);
      }
    } else if (type === NODE_TYPES.ModelNode) {
      if (
        modelLabelMap.has(nodeName) &&
        !duplicates.modelNode.includes(nodeName)
      ) {
        duplicates.modelNode.push(nodeName);
      } else {
        modelLabelMap.add(nodeName);
      }
    } else if (type === NODE_TYPES.SkillNode) {
      if (
        skillLabelMap.has(nodeName) &&
        !duplicates.skillNode.includes(nodeName)
      ) {
        duplicates.skillNode.push(nodeName);
      } else {
        skillLabelMap.add(nodeName);
      }
    }
  });
  return duplicates;
};

/**
 * Extracts the SHA from a given string, typically formatted as "(SHA)".
 * Returns the SHA if found, otherwise returns null.
 *
 * @param input - The input string containing the SHA.
 * @returns The extracted SHA or null if not found.
 */
export const extractSha = (input: string | undefined): string | null => {
  const match = input?.match(/\(([\da-f]{7,40})\)/i);
  return match ? match[1] : null;
};

export const getModelTypeName = (type: AIModelType): string => {
  switch (type) {
    case AIModelType.Endpoint:
      return "Endpoint";
    case AIModelType.Model:
      return "Model";
    default:
      return "Unknown";
  }
};

export const getModelRegistryName = (registry: CortexRegistryType): string => {
  switch (registry) {
    case CortexRegistryType.VertexAI:
      return "Vertex AI";
    case CortexRegistryType.Tamarind:
      return "Tamarind";
    case CortexRegistryType.SageMaker:
      return "SageMaker";
    default:
      return "Unknown";
  }
};
